/**
 * ScopeBreadcrumbNavigator — Setting-aware breadcrumb navigation (DRR-075).
 *
 * Renders a clickable breadcrumb trail from the scope DAG root down to
 * the currently selected node. Each crumb shows the depth label from
 * the active Setting bundle (e.g., "Health System > Facility > Unit > Resident"
 * for assisted living, or "Agency > Branch > Service Area > Patient"
 * for home care).
 *
 * Uses useScopeGraph().scopePath for breadcrumb data.
 * Uses useLensEvaluation().settingBundle for depth labels.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Badge,
  Breadcrumbs,
  Anchor,
  Group,
  Loader,
  Paper,
  Text,
  Tooltip,
  ActionIcon,
} from '@mantine/core';

import { useScopeGraph } from './hooks/useScopeGraph';
import type { ScopePathData } from './hooks/useScopeGraph';
import { useLensEvaluation } from './hooks/useLensEvaluation';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Depth-based colors matching ScopeDAGNavigator */
const DEPTH_COLORS = [
  '#1864ab', // depth 0 — Organization/System
  '#1971c2', // depth 1 — Facility/Agency
  '#228be6', // depth 2 — Unit/Branch
  '#339af0', // depth 3 — Shift/Service Area
  '#4dabf7', // depth 4 — Assignment/Care Team
  '#74c0fc', // depth 5 — Subject (Resident/Patient)
];

/** Default scope labels when no setting bundle is loaded */
const DEFAULT_SCOPE_LABELS: Record<number, string> = {
  0: 'Organization',
  1: 'Division',
  2: 'Department',
  3: 'Team',
  4: 'Assignment',
  5: 'Subject',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScopeBreadcrumbNavigatorProps {
  /** Currently selected scope node ID. */
  selectedNodeId: string | null;
  /** Callback when a breadcrumb item is clicked (navigate to ancestor). */
  onNodeNavigate?: (actorId: string) => void;
  /** Show depth badge alongside labels. */
  showDepthBadge?: boolean;
  /** Maximum breadcrumb items before truncating with ellipsis. */
  maxVisible?: number;
  /** Override scope labels (from setting bundle). */
  scopeLabels?: Record<number, string>;
  /** Compact mode for narrow layouts. */
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Helper: truncation for long paths
// ---------------------------------------------------------------------------

function truncatePath(
  path: string[],
  maxVisible: number
): { items: string[]; truncated: boolean; skippedCount: number } {
  if (path.length <= maxVisible) {
    return { items: path, truncated: false, skippedCount: 0 };
  }
  // Always show first item (root) and last (maxVisible - 2) items
  const root = path[0] ?? '';
  const tail = path.slice(-(maxVisible - 1));
  return {
    items: [root, ...tail],
    truncated: true,
    skippedCount: path.length - maxVisible,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScopeBreadcrumbNavigator({
  selectedNodeId,
  onNodeNavigate,
  showDepthBadge = true,
  maxVisible = 6,
  scopeLabels,
  compact = false,
}: ScopeBreadcrumbNavigatorProps) {
  const { scopePath, isLoading, fetchScopePath } = useScopeGraph();
  const { settingBundle } = useLensEvaluation();

  // Resolve scope labels: prop override > setting bundle derived > defaults
  const labels = useMemo((): Record<number, string> => {
    if (scopeLabels) return scopeLabels;
    if (settingBundle) {
      // Derive scope labels from SettingBundle's industryLabel (root) and
      // subjectLabel (leaf). Intermediate depths use defaults.
      return {
        ...DEFAULT_SCOPE_LABELS,
        0: settingBundle.industryLabel || DEFAULT_SCOPE_LABELS[0] || 'Organization',
        5: settingBundle.subjectLabel || DEFAULT_SCOPE_LABELS[5] || 'Subject',
      };
    }
    return DEFAULT_SCOPE_LABELS;
  }, [scopeLabels, settingBundle]);

  // Fetch scope path when selected node changes
  useEffect(() => {
    if (selectedNodeId) {
      fetchScopePath(selectedNodeId);
    }
  }, [selectedNodeId, fetchScopePath]);

  const handleNavigate = useCallback(
    (actorId: string) => {
      onNodeNavigate?.(actorId);
    },
    [onNodeNavigate]
  );

  // Empty state
  if (!selectedNodeId) {
    return (
      <Paper p={compact ? 'xs' : 'sm'} withBorder>
        <Text size="sm" c="dimmed">
          Select a scope node to see navigation path
        </Text>
      </Paper>
    );
  }

  // Loading state
  if (isLoading && !scopePath) {
    return (
      <Paper p={compact ? 'xs' : 'sm'} withBorder>
        <Group gap="xs">
          <Loader size={14} />
          <Text size="sm" c="dimmed">Loading scope path...</Text>
        </Group>
      </Paper>
    );
  }

  // No path found
  if (!scopePath || scopePath.path.length === 0) {
    return (
      <Paper p={compact ? 'xs' : 'sm'} withBorder>
        <Text size="sm" c="dimmed">
          No scope path available
        </Text>
      </Paper>
    );
  }

  // Build breadcrumb items with truncation
  const { items, truncated, skippedCount } = truncatePath(
    scopePath.path,
    maxVisible
  );

  const breadcrumbItems = items.map((nodeId, index) => {
    // Calculate real depth (accounting for truncation)
    const realDepth = truncated && index > 0
      ? scopePath.path.indexOf(nodeId)
      : index;
    const depthLabel = labels[realDepth] ?? `Level ${realDepth}`;
    const color = DEPTH_COLORS[Math.min(realDepth, DEPTH_COLORS.length - 1)] ?? '#1864ab';
    const isLast = index === items.length - 1;
    const isEllipsis = truncated && index === 1;

    if (isEllipsis) {
      // Insert ellipsis marker before the truncated item
      return (
        <React.Fragment key={`ellipsis-${nodeId}`}>
          <Tooltip label={`${skippedCount} levels hidden`}>
            <Text size="sm" c="dimmed" style={{ cursor: 'default' }}>
              ...
            </Text>
          </Tooltip>
          <BreadcrumbItem
            nodeId={nodeId}
            depth={realDepth}
            depthLabel={depthLabel}
            color={color}
            isActive={isLast}
            showBadge={showDepthBadge}
            compact={compact}
            onClick={isLast ? undefined : () => handleNavigate(nodeId)}
          />
        </React.Fragment>
      );
    }

    return (
      <BreadcrumbItem
        key={nodeId}
        nodeId={nodeId}
        depth={realDepth}
        depthLabel={depthLabel}
        color={color}
        isActive={isLast}
        showBadge={showDepthBadge}
        compact={compact}
        onClick={isLast ? undefined : () => handleNavigate(nodeId)}
      />
    );
  });

  return (
    <Paper p={compact ? 'xs' : 'sm'} withBorder>
      <Group gap="xs" align="center">
        {showDepthBadge && (
          <Tooltip label={`Depth: ${scopePath.depth}`}>
            <Badge size="xs" variant="filled" color={DEPTH_COLORS[Math.min(scopePath.depth, 5)] ?? '#1864ab'}>
              D{scopePath.depth}
            </Badge>
          </Tooltip>
        )}
        <Breadcrumbs separator="/">
          {breadcrumbItems}
        </Breadcrumbs>
      </Group>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// BreadcrumbItem sub-component
// ---------------------------------------------------------------------------

interface BreadcrumbItemProps {
  nodeId: string;
  depth: number;
  depthLabel: string;
  color: string;
  isActive: boolean;
  showBadge: boolean;
  compact: boolean;
  onClick?: () => void;
}

function BreadcrumbItem({
  nodeId,
  depth,
  depthLabel,
  color,
  isActive,
  showBadge,
  compact,
  onClick,
}: BreadcrumbItemProps) {
  // Shorten node ID for display (last 8 chars or full if short)
  const displayId = nodeId.length > 12
    ? `...${nodeId.slice(-8)}`
    : nodeId;

  const content = (
    <Group gap={4} wrap="nowrap">
      {showBadge && !compact && (
        <Badge size="xs" variant="light" color={color}>
          {depthLabel}
        </Badge>
      )}
      <Text
        size={compact ? 'xs' : 'sm'}
        fw={isActive ? 600 : 400}
        c={isActive ? undefined : 'dimmed'}
      >
        {compact ? displayId : nodeId}
      </Text>
    </Group>
  );

  if (isActive || !onClick) {
    return (
      <Tooltip label={`${depthLabel}: ${nodeId}`}>
        <span>{content}</span>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={`Navigate to ${depthLabel}: ${nodeId}`}>
      <Anchor
        component="button"
        type="button"
        onClick={onClick}
        underline="hover"
        style={{ cursor: 'pointer' }}
      >
        {content}
      </Anchor>
    </Tooltip>
  );
}

export default ScopeBreadcrumbNavigator;
